import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangleIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useReportProblemStore } from './reportProblemStore';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

/**
 * Catches a crash in the React tree and offers to report it with the stack attached.
 *
 * A crashed subtree cannot host a dialog, so this renders a fallback screen instead of
 * opening one; the report dialog itself lives above the boundary at the app root.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    console.error('Unhandled render error', error, info);
  }

  private report = () => {
    const { error, componentStack } = this.state;
    if (!error) return;

    useReportProblemStore.getState().open({
      title: error.message.slice(0, 200) || 'The screen crashed',
      errorStack: [error.stack, componentStack].filter(Boolean).join('\n\nComponent stack:\n'),
    });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-8 text-center text-white">
        <AlertTriangleIcon className="h-10 w-10 text-amber-300" />
        <div className="space-y-1">
          <h1 className="text-xl font-semibold">This screen stopped working</h1>
          <p className="max-w-md text-sm text-white/55">
            Reporting it sends the error and the page context so it can be reproduced.
          </p>
        </div>
        <p className="max-w-lg break-words rounded-xl border border-content-highlight/40 bg-content-dark/60 p-3 text-xs text-white/60">
          {error.message}
        </p>
        <div className="flex gap-2">
          <Button type="button" onClick={this.report}>
            Report this
          </Button>
          <Button type="button" variant="ghost-light" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </div>
      </div>
    );
  }
}
